import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Lock, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAdminSettings, useSaveAdminSetting, type AdminSetting, type SettingModule } from '@/api/settings';
import { ROUTES } from '@/lib/routes';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { cn } from '@/lib/utils';

/**
 * Admin → <module> → Settings. Rendered straight from the server registry
 * (server/moduleSettings.ts): live settings are editable, `env` ones are shown read-only
 * (they stay in App Service config on purpose), `planned` ones are roadmap placeholders.
 */

const TITLES: Record<SettingModule, { title: string; blurb: string; related?: { to: string; label: string }[] }> = {
  system: { title: 'System settings', blurb: 'Cross-module settings for sign-in and auditing.' },
  psa: {
    title: 'PSA settings', blurb: 'Autotask defaults for tickets, time and notes.',
    related: [{ to: ROUTES.admin.aiAssistant, label: 'AI Assistant prompts' }, { to: ROUTES.admin.webhook, label: 'Webhook' }],
  },
  rmm: { title: 'RMM settings', blurb: 'Datto RMM dashboards and background jobs.' },
  cipp: {
    title: 'CIPP settings', blurb: 'Microsoft 365 security-posture rules.',
    related: [{ to: ROUTES.admin.cipp, label: 'Tenant ↔ company mapping' }],
  },
  csp: { title: 'CSP settings', blurb: 'NCE renewal thresholds and licence-request approvals. Applies to everyone.' },
  backup: { title: 'Backup settings', blurb: 'Datto SaaS Protection health rules and background refresh.' },
  insights: { title: 'Insights settings', blurb: 'Cross-module coverage and check-in drift rules.' },
  network: { title: 'Network settings', blurb: 'Sophos firewall and UniFi rules.', related: [{ to: ROUTES.admin.network, label: 'Sophos / UniFi ↔ company mapping' }] },
  domains: { title: 'Domains settings', blurb: 'DMARC enforcement rules (dmarcian + CIPP).' },
  soc: { title: 'SOC settings', blurb: 'Huntress agent check-in and stale rules.', related: [{ to: ROUTES.admin.mappings, label: 'Huntress org ↔ company mapping' }] },
};

export default function AdminModuleSettingsPage({ module }: { module: SettingModule }) {
  const t = TITLES[module];
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-lg font-semibold">{t.title}</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">{t.blurb}</p>
        {t.related && (
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            {t.related.map((r) => <Link key={r.to} to={r.to} className="rounded-md border border-border px-2 py-0.5 text-muted-foreground hover:border-ring hover:text-foreground">{r.label} →</Link>)}
          </div>
        )}
      </div>
      <ModuleSettingsList module={module} />
    </div>
  );
}

/** The settings of one module, grouped. Also embedded on Admin → System. */
export function ModuleSettingsList({ module }: { module: SettingModule }) {
  const { data, isLoading, error } = useAdminSettings();
  const groups = useMemo(() => {
    const m = new Map<string, AdminSetting[]>();
    for (const s of data ?? []) if (s.module === module) m.set(s.group, [...(m.get(s.group) ?? []), s]);
    return [...m.entries()];
  }, [data, module]);

  if (isLoading) return <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  if (error) return <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error instanceof Error ? error.message : 'Failed to load settings'}</div>;
  if (!groups.length) return <p className="text-sm text-muted-foreground">No settings for this module yet.</p>;

  return (
    <div className="space-y-5">
      {groups.map(([group, items]) => (
        <section key={group}>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{group}</h2>
          <div className="divide-y divide-divider overflow-hidden rounded-lg border border-border bg-card shadow-sm">
            {items.map((s) => <SettingRow key={s.key} s={s} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

function SettingRow({ s }: { s: AdminSetting }) {
  const save = useSaveAdminSetting();
  const disabled = !!s.planned || s.type === 'env';

  const commit = (value: unknown | undefined) =>
    save.mutate({ key: s.key, value }, {
      onSuccess: () => toast.success(value === undefined ? `${s.label} reset to default` : `${s.label} saved`),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not save'),
    });

  return (
    <div className={cn('flex flex-col gap-3 px-5 py-4 md:flex-row md:items-start md:justify-between md:gap-8', s.planned && 'opacity-70')}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground">{s.label}</span>
          {s.planned && <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Planned</span>}
          {s.type === 'env' && <span className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground"><Lock className="h-2.5 w-2.5" />{s.envVar}</span>}
          {s.public && !s.planned && <span className="rounded-full bg-info-soft px-1.5 py-0.5 text-[10px] font-semibold text-info" title="Read by every user's browser">Applies to everyone</span>}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">{s.description}</p>
        {!s.isDefault && s.updatedAt && s.type !== 'env' && (
          <p className="mt-1 text-[11px] text-muted-foreground/80">Changed {fmtRelative(s.updatedAt)}{s.updatedBy ? ` by ${s.updatedBy}` : ''}</p>
        )}
        {s.type === 'env' && <p className="mt-1 text-[11px] text-muted-foreground/80">Set in the App Service environment, not here.</p>}
      </div>
      <div className="flex shrink-0 items-start gap-1.5">
        <Editor s={s} disabled={disabled || save.isPending} onCommit={commit} />
        {!disabled && !s.isDefault && (
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Reset to default" onClick={() => commit(undefined)} disabled={save.isPending}>
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

function Editor({ s, disabled, onCommit }: { s: AdminSetting; disabled: boolean; onCommit: (v: unknown) => void }) {
  switch (s.type) {
    case 'boolean':
      return (
        <button type="button" role="switch" aria-checked={s.value} disabled={disabled} onClick={() => onCommit(!s.value)}
          className={cn('relative mt-1 h-5 w-9 rounded-full transition-colors disabled:cursor-not-allowed', s.value ? 'bg-primary' : 'bg-muted-foreground/30')}>
          <span className={cn('absolute top-0.5 h-4 w-4 rounded-full bg-background shadow transition-transform', s.value ? 'translate-x-[18px]' : 'translate-x-0.5')} />
        </button>
      );
    case 'number':
      return <NumberEditor s={s} disabled={disabled} onCommit={onCommit} />;
    case 'emails':
      return <ListEditor value={s.value} disabled={disabled} onCommit={onCommit} />;
    case 'list':
      return <ListEditor value={s.value} disabled={disabled} onCommit={onCommit} placeholder={s.placeholder} hint={s.patternHint} />;
    case 'select':
      return (
        <select value={s.value} disabled={disabled} onChange={(e) => onCommit(e.target.value)}
          className="h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground">
          {s.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    case 'text':
      return <Input defaultValue={s.value} disabled={disabled} maxLength={s.maxLength} className="h-8 w-64" onBlur={(e) => e.target.value !== s.value && onCommit(e.target.value)} />;
    case 'env': {
      const on = ['true', '1', 'yes', 'on'].includes(s.value.toLowerCase());
      return <span className={cn('mt-1 rounded-full px-2 py-0.5 text-xs font-semibold', on ? 'bg-success-soft text-success' : 'bg-muted text-muted-foreground')}>{on ? 'On' : s.value === 'false' ? 'Off' : s.value}</span>;
    }
  }
}

function NumberEditor({ s, disabled, onCommit }: { s: Extract<AdminSetting, { type: 'number' }>; disabled: boolean; onCommit: (v: unknown) => void }) {
  const [text, setText] = useState(String(s.value));
  useEffect(() => { setText(String(s.value)); }, [s.value]);
  const n = Number(text);
  const valid = Number.isInteger(n) && n >= s.min && n <= s.max;
  const dirty = valid && n !== s.value;
  return (
    <div className="flex items-center gap-1.5">
      <Input type="number" value={text} min={s.min} max={s.max} disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && dirty) onCommit(n); }}
        className={cn('h-8 w-24 text-right tabular-nums', String(s.max).length > 5 && 'w-32' /* ids (queue 29682833) */, !valid && 'border-destructive')} />
      {s.unit && <span className="text-xs text-muted-foreground">{s.unit}</span>}
      {dirty && <Button size="sm" className="h-8" onClick={() => onCommit(n)}>Save</Button>}
    </div>
  );
}

/** Emails / domains, one per chip. */
function ListEditor({ value, disabled, onCommit, placeholder = 'name@synfo.com.au', hint }: { value: string[]; disabled: boolean; onCommit: (v: unknown) => void; placeholder?: string; hint?: string }) {
  const [text, setText] = useState('');
  const add = () => {
    const items = text.split(/[,;\s]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
    if (!items.length) return;
    onCommit([...value, ...items]);
    setText('');
  };
  return (
    <div className="w-72 space-y-1.5">
      <div className="flex gap-1.5">
        <Input value={text} disabled={disabled} placeholder={placeholder} title={hint} className="h-8"
          onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
        <Button size="sm" variant="outline" className="h-8" disabled={disabled || !text.trim()} onClick={add}>Add</Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground">
              {v}
              {!disabled && <button type="button" className="text-muted-foreground hover:text-destructive" aria-label={`Remove ${v}`} onClick={() => onCommit(value.filter((x) => x !== v))}>×</button>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

